import { SchoolContentItem } from "@axl-dev/school-sdk";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const findContentItem = (
  items: SchoolContentItem[],
  id: string,
): SchoolContentItem | null => {
  for (const item of items) {
    if (item.id === id) return item;

    const found = findContentItem(item.children ?? [], id);
    if (found) return found;
  }

  return null;
};

export function useContentFolder(items: SchoolContentItem[], param: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = useMemo(
    () => searchParams.get(param),
    [searchParams, param],
  );

  const folder = useMemo(
    () => (folderId ? findContentItem(items, folderId) : null),
    [items, folderId],
  );

  const setFolderParam = useCallback(
    (id: string | null, replace: boolean) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (id) params.set(param, id);
          else params.delete(param);

          return params;
        },
        { replace },
      );
    },
    [param, setSearchParams],
  );

  const openFolder = useCallback(
    (item: SchoolContentItem | null) => {
      const id = item?.id ?? null;
      if (id === folderId) return;

      setFolderParam(id, false);
    },
    [folderId, setFolderParam],
  );

  const resetFolder = useCallback(
    () => setFolderParam(null, true),
    [setFolderParam],
  );

  return { folder, openFolder, resetFolder };
}
